import { FormattedPoValues } from '../../../models/poData/FormattedPoValues';
import { PoEmailData } from '../../../models/poData/FormattedContacts';
import { TechZones } from '../../../models/assets-data/TechZonesConfig.interface';
import { LogType } from '../../../models/logs/LogType.enum';
import { resolveShelfLocation } from './shelf-location.rules';
import { deriveInitialContacts } from './contacts.rules';
import { logTypeFromJobType } from './log-type.rules';

/**
 * Values to apply to the log form controls after a successful PO lookup.
 */
export interface PoFormPrefillValues {
  /** Numeric unless the PO number contains letters, in which case the raw string. */
  poNumber: string | number;
  vendor: string;
  jobNumber: string;
  jobName: string;
  jcDept: string;
  notify: string;
  contact: string;
}

/**
 * Everything derived from fetched PO data that a receiving log entry needs,
 * independent of which UI (desktop form or mobile flow) consumes it.
 */
export interface PoPrefill {
  logType: LogType | null;
  shelfLocation: string;
  primaryContact: string;
  notifyList: PoEmailData[];
  notifyListString: string;
  formValues: PoFormPrefillValues;
}

/**
 * Derives all log-entry prefill values from fetched PO data: the log type
 * (Project/Service), the shelf location (tech zone or delivery location),
 * the primary contact, the notify list, and the form field values.
 *
 * @param poData - The formatted PO data containing job, contact, and location information.
 * @param techZones - Tech zone configuration used to resolve Service shelf locations.
 * @param poValue - The PO number the user entered.
 * @param poValueHasLetters - Whether the PO number contains letters (kept as string if so).
 * @returns The derived prefill values.
 */
export function derivePoPrefill(
  poData: FormattedPoValues,
  techZones: TechZones,
  poValue: string,
  poValueHasLetters: boolean
): PoPrefill {
  const jobInformation = poData.jobInformation;
  const contactInformation = poData.contactInformation;

  const shelfLocation = resolveShelfLocation(poData.locationInformation, techZones);

  // Employee-only pre-checking (ado-11284) and the "primary must be someone we're
  // emailing" rule live in deriveInitialContacts, shared with the mobile flow.
  const { notifyList, notifyListString, primaryContact } = deriveInitialContacts(contactInformation);

  return {
    logType: logTypeFromJobType(jobInformation.jobType),
    shelfLocation,
    primaryContact,
    notifyList,
    notifyListString,
    formValues: {
      poNumber: poValueHasLetters ? poValue : Number(poValue),
      vendor: jobInformation.vendor,
      jobNumber: jobInformation.jobNumber,
      jobName: jobInformation.jobName,
      jcDept: jobInformation.jcDept,
      notify: notifyListString,
      contact: primaryContact,
    },
  };
}
